import { type SimulationSeed, simulationSeed } from '@kenocyber/shared';
import {
  PVP_ARENA_HEIGHT_MU,
  PVP_ARENA_WIDTH_MU,
  PVP_BOOSTERS,
  PVP_RUNE_DURATION_TICKS,
  PVP_RUNE_KINDS,
  PVP_RUNE_MAX_TELEPORT_CHARGES,
  PVP_RUNE_PADS,
  PVP_RUNE_PICKUP_RADIUS_MU,
  PVP_RUNE_RESPAWN_TICKS,
  PVP_SHIP_RADIUS_MU,
  type PvpRuneKind,
} from './balance.js';

/**
 * What a duel picks up, and what that does to a ship.
 *
 * ## Every one of these is the room's decision
 *
 * A client says which keys are down and where it is pointing. It does not say
 * that it stood on a pad, that its damage is doubled, or that it has a teleport
 * left - all of that is computed here, from positions the room already owns,
 * and sent back as state. A duel where the loser's browser can grant itself
 * Double Damage is not a duel.
 *
 * ## And every one of them is deterministic
 *
 * Which rune a pad grows is drawn from the SIMULATION generator, whose state
 * lives in the snapshot. A room evicted mid-match and rebuilt from storage
 * therefore continues the same sequence rather than starting a new one. The
 * economic contracts are not available to it and must never be: their entropy
 * never leaves the server as state, so a restored room could not reproduce a
 * draw, and a duel pays nothing that would justify one.
 */

/** A pad, and whatever is standing on it. */
export interface PvpRuneState {
  /** Index into {@link PVP_RUNE_PADS}. The position is never sent as state. */
  readonly pad: number;
  /** What is on the pad now, or `null` while it is regrowing. */
  kind: PvpRuneKind | null;
  /** The tick the pad next produces something. Ignored while `kind` is set. */
  readyAt: number;
}

/**
 * What a ship is currently under the influence of.
 *
 * Ticks REMAINING rather than an expiry tick, so the numbers stay small and a
 * snapshot restored into a room whose clock resumed elsewhere cannot expire an
 * effect early or late.
 */
export interface PvpShipEffects {
  doubleDamageTicks: number;
  speedTicks: number;
  teleportCharges: number;
}

export function pvpNoEffects(): PvpShipEffects {
  return { doubleDamageTicks: 0, speedTicks: 0, teleportCharges: 0 };
}

/** Every pad, empty, at the start of a match. */
export function pvpInitialRunes(firstSpawnTick: number): PvpRuneState[] {
  return PVP_RUNE_PADS.map((_pad, index) => ({
    pad: index,
    kind: null,
    readyAt: firstSpawnTick,
  }));
}

/**
 * Grows whatever is due, drawing a kind for each pad that has come back.
 *
 * The generator is advanced ONLY when a pad actually produces something, so the
 * sequence a match draws is a function of what happened in it rather than of
 * how long it ran. Two rooms that played identically draw identically.
 */
export function pvpGrowRunes(
  runes: PvpRuneState[],
  tick: number,
  nextKind: () => PvpRuneKind,
): void {
  for (const rune of runes) {
    if (rune.kind === null && tick >= rune.readyAt) {
      rune.kind = nextKind();
    }
  }
}

/** Which rune index the given kind is, for an unbiased draw over all four. */
export function pvpRuneKindAt(index: number): PvpRuneKind {
  const kind = PVP_RUNE_KINDS[index];
  if (!kind) {
    throw new RangeError(`no rune kind at index ${index}`);
  }
  return kind;
}

/**
 * Applies a rune to a ship, or answers that it was not taken.
 *
 * A refusal is a real answer and not an error: a player already holding a
 * teleport charge walks over a teleport rune without consuming it, and the rune
 * stays for the opponent. Anything else would let a player deny a pickup by
 * standing on it with a full hand.
 */
export function pvpApplyRune(effects: PvpShipEffects, kind: PvpRuneKind): boolean {
  switch (kind) {
    case 'double_damage':
      effects.doubleDamageTicks = PVP_RUNE_DURATION_TICKS.double_damage;
      return true;
    case 'speed':
      effects.speedTicks = PVP_RUNE_DURATION_TICKS.speed;
      return true;
    case 'teleport':
      if (effects.teleportCharges >= PVP_RUNE_MAX_TELEPORT_CHARGES) {
        return false;
      }
      effects.teleportCharges += 1;
      return true;
  }
}

/**
 * Takes any rune this hull is standing on.
 *
 * Distance from the pad's CENTRE to the hull's centre, against the pickup
 * radius plus the hull's own - so a ship drawn larger does not reach further,
 * because the radius here is the collision one and the drawing cannot move it.
 */
export function pvpCollectRunes(
  runes: PvpRuneState[],
  ship: { readonly x: number; readonly y: number },
  effects: PvpShipEffects,
  tick: number,
): PvpRuneKind[] {
  const taken: PvpRuneKind[] = [];
  for (const rune of runes) {
    if (rune.kind === null) {
      continue;
    }
    if (pvpRuneReachSquared(rune, ship) === null) {
      continue;
    }
    if (!pvpApplyRune(effects, rune.kind)) {
      // Refused. The rune stays exactly where it was, for whoever wants it.
      continue;
    }
    taken.push(rune.kind);
    rune.kind = null;
    rune.readyAt = tick + PVP_RUNE_RESPAWN_TICKS;
  }
  return taken;
}

/**
 * How far this hull's centre is from the pad, squared - or `null` if out of reach.
 *
 * The one place the reach is computed, so the single-ship path and the contested
 * path below cannot drift apart. Squared because nothing needs the root: a
 * comparison and an ordering both work on the square, and `isqrt` would throw
 * away exactly the precision a tie-break depends on.
 */
export function pvpRuneReachSquared(
  rune: PvpRuneState,
  ship: { readonly x: number; readonly y: number },
): number | null {
  const pad = PVP_RUNE_PADS[rune.pad];
  if (!pad) {
    return null;
  }
  const reach = PVP_RUNE_PICKUP_RADIUS_MU + PVP_SHIP_RADIUS_MU;
  const dx = ship.x - pad.x;
  const dy = ship.y - pad.y;
  const distanceSquared = dx * dx + dy * dy;
  return distanceSquared > reach * reach ? null : distanceSquared;
}

/** Anything that can stand on a pad. Deliberately not a ship: this is geometry. */
export interface PvpRuneClaimant {
  /** A vida em MILLI: um destroço não apanha uma runa (gate V28G.S.1). */
  readonly healthMilli: bigint;
  readonly x: number;
  readonly y: number;
  readonly effects: PvpShipEffects;
}

/** Who took the pad, as an index into the claimants that were offered it. */
export interface PvpRuneClaim {
  readonly claimant: number;
  readonly kind: PvpRuneKind;
}

/**
 * Hands one pad to one of the ships standing on it.
 *
 * ## Why a pad needs a rule at all now
 *
 * With two ships it did not: they were collected one after the other, and the
 * one collected FIRST took whatever both were standing on. That is a decision
 * made by iteration order, and with six ships iteration order is exactly what
 * must stop deciding anything - a1 beating b1 to every contested pad would be
 * an advantage the server handed out for being first in an array.
 *
 * ## The rule
 *
 * The NEAREST hull takes it, by the same squared distance the reach test
 * already computes - no new metric, no new constant. An exact tie goes to the
 * earlier participant in canonical order, which is why `claimants` must be
 * given in that order and why the answer does not depend on how the caller
 * built its list.
 *
 * A refusal is not a failure and does not end the tick: a player already
 * holding a teleport charge declines the rune, and it is offered to the next
 * ship standing on it - the same "a full hand cannot deny a pickup" rule
 * {@link pvpApplyRune} states, extended from two ships to six.
 *
 * Dead ships are not claimants. A hull that reaches zero on the tick it drifts
 * over a pad takes nothing.
 */
export function pvpClaimRune(
  rune: PvpRuneState,
  claimants: readonly PvpRuneClaimant[],
  tick: number,
): PvpRuneClaim | null {
  const kind = rune.kind;
  if (kind === null) {
    return null;
  }
  const standing: { readonly index: number; readonly distanceSquared: number }[] = [];
  for (const [index, claimant] of claimants.entries()) {
    if (claimant.healthMilli <= 0n) {
      continue;
    }
    const distanceSquared = pvpRuneReachSquared(rune, claimant);
    if (distanceSquared === null) {
      continue;
    }
    standing.push({ index, distanceSquared });
  }
  standing.sort(
    (left, right) => left.distanceSquared - right.distanceSquared || left.index - right.index,
  );
  for (const candidate of standing) {
    const claimant = claimants[candidate.index];
    if (!claimant || !pvpApplyRune(claimant.effects, kind)) {
      continue;
    }
    rune.kind = null;
    rune.readyAt = tick + PVP_RUNE_RESPAWN_TICKS;
    return { claimant: candidate.index, kind };
  }
  return null;
}

/** One tick off every timer. Charges are not timers and are never decayed. */
export function pvpAgeEffects(effects: PvpShipEffects): void {
  if (effects.doubleDamageTicks > 0) effects.doubleDamageTicks -= 1;
  if (effects.speedTicks > 0) effects.speedTicks -= 1;
}

/** A dead ship keeps nothing: no timer and no charge. */
export function pvpClearEffects(effects: PvpShipEffects): void {
  effects.doubleDamageTicks = 0;
  effects.speedTicks = 0;
  effects.teleportCharges = 0;
}

/**
 * The extra displacement every booster this hull is inside contributes.
 *
 * Summed rather than taken from the first match, because overlapping boosters
 * are a map decision and a ship in two of them should feel both. There are none
 * overlapping today; the rule is here so adding one is not a silent change.
 */
export function pvpBoosterPush(ship: { readonly x: number; readonly y: number }): {
  x: number;
  y: number;
} {
  let x = 0;
  let y = 0;
  for (const booster of PVP_BOOSTERS) {
    if (
      ship.x >= booster.x &&
      ship.x <= booster.x + booster.w &&
      ship.y >= booster.y &&
      ship.y <= booster.y + booster.h
    ) {
      x += booster.pushX;
      y += booster.pushY;
    }
  }
  return { x, y };
}

/** Every way a chosen destination can be refused. One reason per rule. */
export type PvpTeleportRefusal =
  | 'no_charge'
  | 'dead'
  | 'not_a_point'
  | 'outside_arena'
  | 'inside_structure';

export type PvpTeleportVerdict =
  | { readonly ok: true; readonly x: number; readonly y: number }
  | { readonly ok: false; readonly reason: PvpTeleportRefusal };

/**
 * May this ship go to this point, and where exactly does it land?
 *
 * ## What changed, and why the old one was the wrong feature
 *
 * This used to take the ship's AIM and a fixed distance, which made a dash: the
 * player pressed a key and slid 600 units forward. The design asks for a
 * teleport - arm a mode, choose an area, and let the server decide - and a
 * fixed vector cannot express choosing.
 *
 * So the point comes from the player. It is an ARGUMENT, in exactly the way the
 * aim vector already is, and everything that decides an outcome is checked
 * here: a client naming a destination is not a client reaching it.
 *
 * ## Refused, never adjusted
 *
 * A destination inside a wall does not become the nearest legal point, and one
 * outside the arena does not become the edge. Both would hand a player a move
 * they did not ask for - and a shortened teleport is a free nudge out of the
 * corner they were supposed to be stuck in. The verdict is binary, the charge
 * is spent only on `ok`, and the reason never describes anything the player
 * could not already see: the arena and its structures are public geometry.
 *
 * `obstructed` is passed in rather than imported so this stays a function of
 * its arguments - the caller already owns the arena's collision answer, and two
 * copies of it would eventually disagree.
 */
export function pvpValidateTeleport(
  point: { readonly x: number; readonly y: number },
  ship: { readonly healthMilli: bigint },
  effects: PvpShipEffects,
  obstructed: (x: number, y: number) => boolean,
): PvpTeleportVerdict {
  if (effects.teleportCharges <= 0) {
    return { ok: false, reason: 'no_charge' };
  }
  if (ship.healthMilli <= 0n) {
    return { ok: false, reason: 'dead' };
  }
  if (!Number.isSafeInteger(point.x) || !Number.isSafeInteger(point.y)) {
    /*
     * Integers on the domain's own scale, and nothing else. A float would put
     * the room and a re-simulation one bit apart forever; NaN and Infinity
     * would pass every comparison below and land a ship nowhere.
     */
    return { ok: false, reason: 'not_a_point' };
  }
  /*
   * The WHOLE hull has to fit, which is why the bound is inset by a radius on
   * every side rather than being the wall itself. A centre that is legal while
   * the hull hangs through a wall is the same defect as a shot that tunnels.
   */
  if (
    point.x < PVP_SHIP_RADIUS_MU ||
    point.y < PVP_SHIP_RADIUS_MU ||
    point.x > PVP_ARENA_WIDTH_MU - PVP_SHIP_RADIUS_MU ||
    point.y > PVP_ARENA_HEIGHT_MU - PVP_SHIP_RADIUS_MU
  ) {
    return { ok: false, reason: 'outside_arena' };
  }
  if (obstructed(point.x, point.y)) {
    return { ok: false, reason: 'inside_structure' };
  }
  // The server's own answer, which is what the event carries to both clients.
  return { ok: true, x: point.x, y: point.y };
}

/**
 * How many bits of private entropy stand behind a duel's runes.
 *
 * 128, and not fewer. A seed a person could enumerate is a seed a person will
 * enumerate: the whole sequence of a match is one offline search away from
 * anybody who watches the first two pads and knows the algorithm.
 */
export const PVP_SEED_BITS = 128;

/** The private seed as it is stored and passed around: 32 lower-case hex chars. */
export const PVP_SEED_HEX_LENGTH = PVP_SEED_BITS / 4;

const SEED_PATTERN = /^[0-9a-f]{32}$/;

/** Is this the exact shape a stored private seed has? Used by the minter too. */
export function pvpIsPrivateSeed(value: unknown): value is string {
  return typeof value === 'string' && SEED_PATTERN.test(value);
}

/**
 * 128 bits of Web Crypto entropy, as hex.
 *
 * ## Why this is not derived from the match id
 *
 * It used to be. `pvpSeedFromMatchId` hashed the id, and the id is public - it
 * is in the URL a player pastes, the ticket request, the socket query string,
 * the welcome frame and the audit row. Anybody holding it could reproduce every
 * rune the match would ever grow.
 *
 * The old argument for that was "a duel pays nothing, so predicting a rune buys
 * nothing", and it was wrong for a reason that has nothing to do with money:
 * knowing the centre pad will grow Double Damage in four seconds lets a player
 * be standing on it when it does. Foreknowledge buys POSITION, and position is
 * the whole game.
 *
 * ## Why Web Crypto and never `Math.random`
 *
 * `Math.random` is seeded by the runtime, unspecified, and in some engines
 * recoverable from a handful of outputs. This is the same generator the rest of
 * the repository uses wherever a value must not be guessable, and it exists in
 * workerd, in Node and in the browser alike.
 *
 * ## What this is NOT
 *
 * Not an economic secret. It decides no reward - a duel pays nothing - so it
 * needs no key id, no audit record and no rotation. It is private for the same
 * reason a shuffled deck is face down: not because the cards are worth money,
 * but because a game where one player has read them is not a game.
 */
/**
 * Where the minting itself lives, and why it is not here.
 *
 * `packages/game-domain` is platform-neutral by rule - no browser APIs, no
 * `process`, no I/O, and a tsconfig with `"types": []` that enforces it. Web
 * Crypto is a platform API, so the 128 bits are minted by the Durable Object
 * (`apps/api/src/pvp/seed.ts`), which is also the only thing that can persist
 * them. What stays here is the pure half: the shape a seed must have, and how
 * private material and a public id are folded into a generator seed.
 *
 * That split is not bureaucracy. It is what keeps this package testable without
 * a runtime and re-simulatable in another language.
 */

/**
 * Folds the private seed and the public match id into the generator's seed.
 *
 * The id participates as CONTEXT and never as the source: two rooms that
 * somehow carried the same id still differ, because the private half differs,
 * while one room is still one room. Mixing it in costs nothing and means a
 * private seed reused by a bug does not produce two identical matches.
 *
 * The generator consumes 64 bits, so the 128 are folded rather than truncated -
 * every bit of the private seed reaches the state.
 *
 * @throws RangeError for anything that is not exactly the stored shape. A
 * malformed seed must fail closed rather than quietly become a different, and
 * possibly guessable, sequence.
 */
export function pvpRoomSeed(privateSeed: string, matchId: string): SimulationSeed {
  if (!SEED_PATTERN.test(privateSeed)) {
    // Never echo the value: an error message is a place secrets go to be read.
    throw new RangeError(`pvp private seed must be ${PVP_SEED_HEX_LENGTH} lower-case hex chars`);
  }
  const mask = (1n << 64n) - 1n;
  const material = BigInt(`0x${privateSeed}`);
  // Fold the high half onto the low half, then stir the id in as context.
  let hash = ((material >> 64n) ^ (material & mask)) & mask;
  const prime = 0x1_0000_01b3n;
  for (let index = 0; index < matchId.length; index += 1) {
    hash = ((hash ^ BigInt(matchId.charCodeAt(index))) * prime) & mask;
  }
  return simulationSeed(hash);
}
